import React, { useState, useCallback } from 'react';
import { APIProvider, Map, AdvancedMarker, Pin, InfoWindow } from '@vis.gl/react-google-maps';
import { X, MapPin as MapPinIcon, DollarSign, Clock, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function MapView({ restaurants, center, onClose }) {
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const mapCenter = center || { lat: 37.7749, lng: -122.4194 };

  const getPinColor = (restaurant) => {
    const specialCount = restaurant.specials?.length || 0;
    if (specialCount === 0) return '#9CA3AF'; // gray
    if (specialCount >= 3) return '#EF4444'; // red (hot deal!)
    if (specialCount >= 2) return '#F59E0B'; // orange
    return '#10B981'; // green
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-white">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-white shadow-md p-4 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Map View</h2>
          <p className="text-sm text-gray-600">{restaurants.length} restaurants with specials</p>
        </div>
        <Button
          variant="outline"
          onClick={onClose}
          data-testid="close-map-view"
        >
          <X className="w-4 h-4 mr-2" />
          Back to List
        </Button>
      </div>

      {/* Map Legend */}
      <div className="absolute top-20 left-4 z-10 bg-white shadow-lg rounded-lg p-3 text-xs">
        <div className="font-semibold mb-2">Legend:</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span>3+ Specials</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
            <span>2 Specials</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span>1 Special</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-gray-400"></div>
            <span>No Specials</span>
          </div>
        </div>
      </div>

      {/* Google Map */}
      <APIProvider apiKey={MAPS_API_KEY}>
        <Map
          mapId="on-the-cheap-map"
          defaultCenter={mapCenter}
          defaultZoom={13}
          gestureHandling="greedy"
          disableDefaultUI={false}
          className="w-full h-full"
        >
          {restaurants.map((restaurant) => {
            const position = {
              lat: restaurant.location?.latitude || 0,
              lng: restaurant.location?.longitude || 0
            };

            return (
              <AdvancedMarker
                key={restaurant.id}
                position={position}
                onClick={() => setSelectedRestaurant(restaurant)}
                onMouseEnter={() => setHoveredId(restaurant.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <Pin
                  background={getPinColor(restaurant)}
                  glyphColor="#fff"
                  borderColor={hoveredId === restaurant.id ? '#1F2937' : getPinColor(restaurant)}
                  scale={hoveredId === restaurant.id ? 1.2 : 1}
                />
              </AdvancedMarker>
            );
          })}

          {/* Info Window */}
          {selectedRestaurant && (
            <InfoWindow
              position={{
                lat: selectedRestaurant.location?.latitude || 0,
                lng: selectedRestaurant.location?.longitude || 0
              }}
              onCloseClick={() => setSelectedRestaurant(null)}
            >
              <div className="p-2 max-w-sm">
                <h3 className="font-bold text-lg text-gray-900 mb-2">
                  {selectedRestaurant.name}
                </h3>
                
                {selectedRestaurant.rating && (
                  <div className="flex items-center mb-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                    <span className="text-sm font-medium">{selectedRestaurant.rating}</span>
                    {selectedRestaurant.price_level && (
                      <span className="ml-2 text-sm text-gray-600">
                        {'$'.repeat(selectedRestaurant.price_level)}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-600 mb-3 flex items-start">
                  <MapPinIcon className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                  {selectedRestaurant.address}
                </p>

                {selectedRestaurant.specials && selectedRestaurant.specials.length > 0 ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-sm text-gray-900">
                      Current Specials:
                    </p>
                    {selectedRestaurant.specials.slice(0, 2).map((special) => (
                      <div key={special.id} className="bg-orange-50 p-2 rounded">
                        <p className="font-medium text-sm text-gray-900">{special.title}</p>
                        <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                          <span className="flex items-center text-green-600 font-medium">
                            <DollarSign className="w-3 h-3" />
                            {special.price}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatTime(special.time_start)} - {formatTime(special.time_end)}
                          </span>
                        </div>
                      </div>
                    ))}
                    {selectedRestaurant.specials.length > 2 && (
                      <p className="text-xs text-gray-500 italic">
                        +{selectedRestaurant.specials.length - 2} more special(s)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {selectedRestaurant.specials_message || 'No current specials'}
                  </p>
                )}
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
  );
}

export default MapView;
